import React from 'react';
import { Section } from '@/pages/LandingComplete';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle } from 'lucide-react';

interface ChristmasContent {
  badge?: string;
  title?: string;
  titleHighlight?: string;
  subtitle?: string;
  pricing?: string;
  ctaPrimary?: string;
  ctaSecondary?: string;
  cards?: Array<{
    emoji: string;
    title: string;
    items: string[];
  }>;
}

export function DynamicChristmas({ content }: { content: ChristmasContent }) {
  const cards = content.cards || [];

  return (
    <Section className="bg-gradient-to-br from-red-50 via-white to-green-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-32 h-32 bg-red-100 rounded-full blur-3xl opacity-50"></div>
      <div className="absolute bottom-0 right-0 w-32 h-32 bg-green-100 rounded-full blur-3xl opacity-50"></div>
      
      <div className="relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-5 py-2 text-red-700 font-semibold mb-6">
            <span className="text-xl">🎄</span>
            {content.badge || 'Weihnachts-Spezial'}
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
            {content.title || 'Neues Gerät zu'} <span className="text-red-600">{content.titleHighlight || 'Weihnachten?'}</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {content.subtitle || 'Tablet, Smartphone oder Laptop geschenkt bekommen? Ich helfe bei der Einrichtung!'}
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {cards.map((card, i) => {
            const borderColors = ['border-red-100 hover:border-red-300', 'border-green-100 hover:border-green-300', 'border-blue-100 hover:border-blue-300'];
            return (
              <Card key={card.title + i} className={`p-6 bg-white/80 backdrop-blur border-2 ${borderColors[i % 3]} transition-all hover:shadow-lg`}>
                <div className="text-4xl mb-4">{card.emoji}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{card.title}</h3>
                <ul className="space-y-2 text-slate-600">
                  {card.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <CheckCircle size={16} className="mt-1 text-green-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-bold rounded-xl shadow-lg"
            >
              🎁 {content.ctaPrimary || 'Termin vereinbaren'}
            </Button>
            <a href="/gutschein">
              <Button
                variant="outline"
                className="border-2 border-red-300 text-red-700 hover:bg-red-50 px-8 py-6 text-lg font-bold rounded-xl"
              >
                🎄 {content.ctaSecondary || 'Als Gutschein verschenken'}
              </Button>
            </a>
          </div>
          {content.pricing && (
            <p className="mt-4 text-slate-600 font-semibold">{content.pricing}</p>
          )}
        </div>
      </div>
    </Section>
  );
}
